import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, TextField, IconButton, Chip, Switch,
  FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Divider, Card, CardContent, CardActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import { getPolls, savePoll, deletePoll } from "../firebaseService";
import { useGroup } from "../context/GroupContext";

const PollManagementPage = () => {
  const { activeGroup } = useGroup();
  const memberLabel = activeGroup?.memberLabel || "Mitglieder";

  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPoll, setEditPoll] = useState(null); // null = geschlossen, {} = neu, {id:...} = bearbeiten
  const [status, setStatus] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadPolls = async () => {
    setLoading(true);
    const data = await getPolls();
    setPolls(data);
    setLoading(false);
  };

  useEffect(() => {
    loadPolls();
  }, []);

  const handleSave = async () => {
    if (!editPoll.title?.trim()) return;
    if (!editPoll.options || editPoll.options.filter((o) => o.trim()).length < 2) {
      setStatus({ type: "error", message: "Mindestens 2 Antwortmoeglichkeiten noetig." });
      return;
    }

    try {
      const cleanOptions = editPoll.options.filter((o) => o.trim());
      await savePoll({
        ...editPoll,
        options: cleanOptions,
        published: editPoll.published || false,
        showOnMemberPage: editPoll.showOnMemberPage || false,
      });
      setEditPoll(null);
      setStatus({ type: "success", message: "Abstimmung gespeichert." });
      await loadPolls();
    } catch (error) {
      setStatus({ type: "error", message: "Fehler beim Speichern: " + error.message });
    }
  };

  const handleDelete = async (pollId) => {
    try {
      await deletePoll(pollId);
      setConfirmDelete(null);
      setStatus({ type: "success", message: "Abstimmung geloescht." });
      await loadPolls();
    } catch (error) {
      setStatus({ type: "error", message: "Fehler beim Loeschen: " + error.message });
    }
  };

  const handleDuplicate = async (poll) => {
    try {
      await savePoll({
        title: `${poll.title || ""} (Kopie)`.trim(),
        description: poll.description || "",
        options: Array.isArray(poll.options) ? [...poll.options] : [],
        color: poll.color || "#5b8def",
        emoji: poll.emoji || "",
        deadline: poll.deadline || "",
        published: false,
        showOnMemberPage: false,
      });
      setStatus({ type: "success", message: "Abstimmung dupliziert (ohne Stimmen)." });
      await loadPolls();
    } catch (error) {
      setStatus({ type: "error", message: "Fehler beim Duplizieren: " + error.message });
    }
  };

  const handleTogglePublished = async (poll) => {
    await savePoll({ ...poll, published: !poll.published });
    await loadPolls();
  };

  const handleToggleShowOnPage = async (poll) => {
    await savePoll({ ...poll, showOnMemberPage: !poll.showOnMemberPage });
    await loadPolls();
  };

  const openNew = () => {
    setEditPoll({
      title: "",
      description: "",
      options: ["", ""],
      color: "#5b8def",
      emoji: "",
      deadline: "",
      published: false,
      showOnMemberPage: false,
    });
  };

  const openEdit = (poll) => {
    setEditPoll({ ...poll, options: [...poll.options] });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Abstimmungen</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Neue Abstimmung
        </Button>
      </Box>

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      {loading ? (
        <Typography>Lade...</Typography>
      ) : polls.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <HowToVoteIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Keine Abstimmungen vorhanden</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Erstelle eine neue Abstimmung, um {memberLabel} abstimmen zu lassen.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openNew}>
            Erste Abstimmung erstellen
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {polls.map((poll) => (
            <Card key={poll.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="h6">
                      {poll.emoji && `${poll.emoji} `}{poll.title}
                    </Typography>
                    {poll.description && (
                      <Typography variant="body2" color="text.secondary">{poll.description}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Chip
                      label={poll.published ? "Aktiv" : "Entwurf"}
                      color={poll.published ? "success" : "default"}
                      size="small"
                    />
                    {poll.showOnMemberPage && (
                      <Chip label="Auf Seite" color="info" size="small" />
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {poll.options.map((opt, i) => (
                    <Chip key={i} label={opt} size="small" variant="outlined" />
                  ))}
                </Box>

                {poll.deadline && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Deadline: {new Date(poll.deadline).toLocaleDateString("de-DE")}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={poll.published} onChange={() => handleTogglePublished(poll)} size="small" />}
                  label="Aktiv"
                />
                <FormControlLabel
                  control={<Switch checked={poll.showOnMemberPage || false} onChange={() => handleToggleShowOnPage(poll)} size="small" />}
                  label="Auf Mitglieder-Seite"
                />
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<HowToVoteIcon />} onClick={() => window.open(`poll-results/${poll.id}`, "_self")}>
                  Ergebnisse
                </Button>
                <IconButton size="small" onClick={() => openEdit(poll)} title="Bearbeiten"><EditIcon /></IconButton>
                <IconButton size="small" onClick={() => handleDuplicate(poll)} title="Duplizieren (ohne Stimmen)"><ContentCopyIcon /></IconButton>
                <IconButton size="small" color="error" onClick={() => setConfirmDelete(poll.id)} title="Loeschen"><DeleteIcon /></IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editPoll} onClose={() => setEditPoll(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editPoll?.id ? "Abstimmung bearbeiten" : "Neue Abstimmung"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Titel"
            fullWidth
            value={editPoll?.title || ""}
            onChange={(e) => setEditPoll({ ...editPoll, title: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Beschreibung (optional)"
            fullWidth
            multiline
            rows={2}
            value={editPoll?.description || ""}
            onChange={(e) => setEditPoll({ ...editPoll, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Emoji (optional)"
              value={editPoll?.emoji || ""}
              onChange={(e) => setEditPoll({ ...editPoll, emoji: e.target.value })}
              sx={{ width: 120 }}
            />
            <TextField
              label="Farbe"
              type="color"
              value={editPoll?.color || "#5b8def"}
              onChange={(e) => setEditPoll({ ...editPoll, color: e.target.value })}
              sx={{ width: 120 }}
              InputProps={{ sx: { height: 56 } }}
            />
            <TextField
              label="Deadline (optional)"
              type="date"
              value={editPoll?.deadline || ""}
              onChange={(e) => setEditPoll({ ...editPoll, deadline: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Antwortmoeglichkeiten</Typography>
          {(editPoll?.options || []).map((opt, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
              <TextField
                value={opt}
                onChange={(e) => {
                  const newOpts = [...editPoll.options];
                  newOpts[i] = e.target.value;
                  setEditPoll({ ...editPoll, options: newOpts });
                }}
                placeholder={`Option ${i + 1}`}
                size="small"
                fullWidth
              />
              {editPoll?.options?.length > 2 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const newOpts = editPoll.options.filter((_, j) => j !== i);
                    setEditPoll({ ...editPoll, options: newOpts });
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setEditPoll({ ...editPoll, options: [...(editPoll?.options || []), ""] })}
          >
            Option hinzufuegen
          </Button>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={editPoll?.published || false}
                onChange={(e) => setEditPoll({ ...editPoll, published: e.target.checked })}
              />
            }
            label="Aktiv (Abstimmung offen)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={editPoll?.showOnMemberPage || false}
                onChange={(e) => setEditPoll({ ...editPoll, showOnMemberPage: e.target.checked })}
              />
            }
            label={`Auf ${activeGroup?.memberSingular || "Mitglieder"}-Seite anzeigen`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPoll(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave} disabled={!editPoll?.title?.trim()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Abstimmung loeschen?</DialogTitle>
        <DialogContent>
          <Typography>Die Abstimmung und alle Stimmen werden unwiderruflich geloescht.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete)}>
            Loeschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PollManagementPage;
